const SESSION_STORAGE_KEY = "chatAiWebSessionId";
const MESSAGES_STORAGE_KEY = "chatAiWebMessages";
const FINISHED_STORAGE_KEY = "chatAiWebFinished";

export function getStoredSessionId() {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

export function setStoredSessionId(id) {
  try {
    if (id) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
      sessionStorage.removeItem(FINISHED_STORAGE_KEY);
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(MESSAGES_STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors in private mode.
  }
}

export function getStoredMessages() {
  try {
    const raw = sessionStorage.getItem(MESSAGES_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(
      (item) =>
        item &&
        typeof item.id === "string" &&
        (item.role === "assistant" || item.role === "user") &&
        typeof item.text === "string" &&
        Array.isArray(item.links)
    );
  } catch {
    return [];
  }
}

export function setStoredMessages(messages) {
  try {
    if (!Array.isArray(messages) || messages.length === 0) {
      sessionStorage.removeItem(MESSAGES_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(MESSAGES_STORAGE_KEY, JSON.stringify(messages));
  } catch {
    // Ignore storage errors in private mode.
  }
}

export function isChatMarkedFinished() {
  try {
    return sessionStorage.getItem(FINISHED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setChatFinished(finished) {
  try {
    if (finished) {
      sessionStorage.setItem(FINISHED_STORAGE_KEY, "1");
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(MESSAGES_STORAGE_KEY);
    } else {
      sessionStorage.removeItem(FINISHED_STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors in private mode.
  }
}

/**
 * @param {{ message: string; sessionId?: string }} body
 * @returns {Promise<{ reply: string; sessionId: string; links: string[] }>}
 */
export async function postWebChat(body) {
  const url = process.env.REACT_APP_CHAT_AI_API_URL;
  const token = process.env.REACT_APP_CHAT_AI_API_TOKEN;

  if (!url || !token) {
    throw new Error(
      "Chat is not configured. Set REACT_APP_CHAT_AI_API_URL and REACT_APP_CHAT_AI_API_TOKEN."
    );
  }

  const payload = { message: body.message };
  if (body.sessionId) {
    payload.sessionId = body.sessionId;
  }

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-token": token,
    },
    body: JSON.stringify(payload),
  });

  let data = {};
  try {
    data = await response.json();
  } catch {
    data = {};
  }

  if (!response.ok) {
    const message =
      typeof data.message === "string" && data.message
        ? data.message
        : `Request failed (${response.status})`;
    throw new Error(message);
  }

  if (typeof data.reply !== "string" || !data.sessionId) {
    throw new Error("Unexpected response from chat service.");
  }

  const links = Array.isArray(data.links)
    ? data.links.filter((value) => typeof value === "string")
    : [];

  return {
    reply: data.reply,
    sessionId: data.sessionId,
    links,
  };
}
